import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";

/**
 * Services page — the studio's four working paths.
 *
 * Editorial, numbered layout (not SaaS pricing cards). Each path is a
 * clearly defined way of working with the studio and CTAs into a tailored
 * quote form via `/quote?service=<slug>`. A secondary consultation CTA
 * lives at the bottom for anyone unsure where to start.
 */

const PATHS = [
  {
    slug: "brand-identity",
    number: "01",
    verb: "Build",
    title: "Brand Identity",
    audience: "For new studios, businesses and ventures beginning at the beginning.",
    body:
      "A complete visual identity, considered from the first sketch. We shape the logo system, palette, type and brand voice into a cohesive world your audience can recognise anywhere — quietly, and on paper as much as on screen.",
    image: "/services/service-3-branding.jpg",
    startingAt: "$2,500",
    deliverables: [
      "Logo system — primary, secondary, marks",
      "Colour palette & typography",
      "Brand guidelines document",
    ],
    cta: "Begin a Brand",
  },
  {
    slug: "brand-boost",
    number: "02",
    verb: "Elevate",
    title: "Brand Boost",
    audience: "For established brands ready for a refresh, a refinement, or a single-asset upgrade.",
    body:
      "Sometimes a brand doesn't need to be rebuilt — it needs to be sharpened. We refine what already exists, extend the visual language into new places, and design the individual pieces that carry a brand quietly forward.",
    image: "/services/service-5-special.jpg",
    startingAt: "$600",
    deliverables: [
      "Logo refresh & mark refinement",
      "Extended brand assets — social, signage, templates",
      "Single-project design — menu, packaging, campaign",
      "Creative direction & art direction",
    ],
    cta: "Elevate Your Brand",
  },
  {
    slug: "print-stationery",
    number: "03",
    verb: "Create",
    title: "Bespoke Print & Stationery",
    shortTitle: "Print & Stationery",
    audience: "For weddings, celebrations, businesses, and the moments worth holding.",
    body:
      "Beautifully considered print — designed, sourced and produced. From save-the-dates and invitations to custom greeting cards and hospitality collateral, we make paper feel like an object worth keeping.",
    image: "/services/service-2-stationery.jpg",
    startingAt: "$500",
    deliverables: [
      "Wedding & event stationery suites",
      "Custom greeting cards & postcards",
      "Personal stationery — notecards, letterheads",
      "Hospitality & retail print collections",
    ],
    cta: "Design Something Printed",
  },
  {
    slug: "custom-artwork",
    number: "04",
    verb: "Commission",
    title: "Custom Artwork",
    audience: "For collectors, hospitality projects, and one-of-a-kind pieces.",
    body:
      "Original illustration and painting made for your project or collection. Editorial commissions, private artwork, hospitality murals, licensed illustration — bring us the brief and we'll create from there.",
    image: "/services/service-1-illustration.jpg",
    startingAt: "$300",
    deliverables: [
      "Original paintings & bespoke artwork",
      "Editorial & publishing illustration",
      "Hospitality & interior commissions",
      "Licensing & product illustration",
    ],
    cta: "Commission a Piece",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="fade-up">
      {/* Editorial intro */}
      <section className="max-w-[1120px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-8 md:pb-12">
        <p className="overline">Working with the Studio</p>
        <div className="mt-6 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-7">
            <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl text-brand-dark leading-[1.05] max-w-3xl">
              Let's create beautiful
              <br />
              things <span className="italic">together.</span>
            </h1>
            <p className="mt-8 md:mt-10 text-brand-neutral text-lg leading-relaxed max-w-2xl">
              Brand identities, bespoke stationery, and original artwork —
              thoughtfully created for businesses, celebrations, and meaningful
              moments.
            </p>
          </div>
          <ul className="md:col-span-5 md:pl-4 divide-y divide-brand-border/60 text-sm">
            {PATHS.map((p) => (
              <li key={p.slug}>
                <a
                  href={`#path-${p.slug}`}
                  data-testid={`services-jump-${p.slug}`}
                  className="group grid grid-cols-[1.25rem_5.75rem_1fr_auto] items-baseline gap-x-4 py-3 text-brand-dark hover:text-brand-accent transition-colors"
                >
                  <span className="text-brand-accent font-serif text-base leading-none">
                    {p.number}
                  </span>
                  <span className="uppercase tracking-[0.16em] text-[10.5px] text-brand-neutral leading-none">
                    {p.verb}
                  </span>
                  <span className="font-serif italic leading-tight min-w-0">
                    {p.shortTitle || p.title}
                  </span>
                  <span
                    data-testid={`services-jump-price-${p.slug}`}
                    className="text-brand-neutral text-xs whitespace-nowrap leading-none"
                  >
                    from {p.startingAt}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* The four paths */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="space-y-24 md:space-y-32">
          {PATHS.map((p, i) => (
            <article
              key={p.slug}
              id={`path-${p.slug}`}
              data-testid={`service-path-${p.slug}`}
              className="grid md:grid-cols-12 gap-10 md:gap-14 items-start scroll-mt-24"
            >
              {/* Image column */}
              <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                <div className="img-zoom aspect-[4/5] overflow-hidden bg-brand-surface max-w-[440px] mx-auto md:mx-0">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                </div>
              </div>

              {/* Text column */}
              <div
                className={`md:col-span-7 ${
                  i % 2 === 1 ? "md:order-1 md:pr-6" : "md:pl-6"
                }`}
              >
                <div className={i % 2 === 1 ? "md:ml-auto md:max-w-[520px]" : "md:max-w-[520px]"}>
                  <div className="flex items-baseline gap-6">
                    <span className="font-serif text-brand-accent text-lg">{p.number}</span>
                    <p className="overline">{p.verb}</p>
                  </div>
                  <h2 className="font-serif font-light text-3xl md:text-4xl mt-4 text-brand-dark leading-[1.1]">
                    {p.title}
                  </h2>
                  <p className="font-serif italic text-brand-accent text-lg mt-5 leading-snug">
                    {p.audience}
                  </p>
                  <p className="mt-6 text-brand-neutral leading-relaxed">{p.body}</p>

                  <div className="mt-10">
                    <p className="overline mb-4">What's included</p>
                    <ul className="space-y-3">
                      {p.deliverables.map((d) => (
                        <li key={d} className="flex items-start gap-4 text-brand-dark leading-relaxed">
                          <span className="mt-2 w-6 border-t border-brand-accent flex-shrink-0" />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    data-testid={`service-price-${p.slug}`}
                    className="mt-10 pt-6 border-t border-brand-border"
                  >
                    <p className="overline text-brand-neutral mb-1">Starting at</p>
                    <p className="font-serif text-brand-dark text-3xl md:text-4xl leading-none">
                      {p.startingAt}
                    </p>
                    <p className="mt-2 text-xs text-brand-neutral italic">
                      Final scope and pricing tailored to your project.
                    </p>
                  </div>

                  <Link
                    to={`/quote?service=${p.slug}`}
                    data-testid={`service-cta-${p.slug}`}
                    className="btn-outline mt-8"
                  >
                    {p.cta} <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Secondary consultation CTA */}
      <section className="bg-brand-dark text-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-12 py-24 md:py-28 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="overline text-white/60">Not sure where to begin?</p>
            <h2 className="font-serif font-light text-3xl md:text-5xl mt-5 leading-[1.1]">
              Book a creative
              <br />
              <span className="italic">consultation.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:pl-6">
            <p className="text-white/75 leading-relaxed">
              Sixty minutes with the studio to talk through your idea, sketch a
              direction, and decide together what shape it should take.
            </p>
            <p className="mt-5 text-white leading-relaxed">
              <span className="font-serif italic text-xl text-white">$150</span>
              <span className="text-white/70"> — credited in full toward any project we take on together.</span>
            </p>
            <Link
              to="/quote?service=consultation"
              data-testid="services-consultation-cta"
              className="btn-ghost-dark mt-10 inline-flex"
            >
              Book a Consultation <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
